import { Building2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSelectedBusinessLine } from '../hooks/useBusinessLines';

/**
 * Global business-line switcher (v1.4 FR-37).
 *
 * Renders nothing when the inventory flag is OFF (FR-29: flag-OFF UI is
 * unchanged) or when the tenant has a single line (FR-33: zero added
 * complexity for single-industry tenants — the default line is auto-selected
 * by useSelectedBusinessLine without any visible control).
 */
export function BusinessLineSwitcher() {
  const { lines, selectedLineId, selectLine, showLineSwitcher } = useSelectedBusinessLine();

  // Visible in exactly one state: an engaged workspace whose unit has no bound
  // line (the tenant-default-unit case). The hook owns the full rule — units
  // loaded, not the group-wide admin state, unit doesn't pin the line — so the
  // switcher can never flicker in before my-units resolves.
  if (!showLineSwitcher) {
    return null;
  }

  return (
    <Select value={selectedLineId ?? undefined} onValueChange={selectLine}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <SelectTrigger
              className="h-9 w-9 justify-center px-0 [&>svg:last-child]:hidden sm:w-[190px] sm:justify-start sm:px-3 sm:[&>svg:last-child]:block"
              aria-label="Switch business line"
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground sm:mr-1" />
              <span className="hidden min-w-0 sm:inline">
                <SelectValue placeholder="Business line" />
              </span>
            </SelectTrigger>
          </TooltipTrigger>
          <TooltipContent>Switch business line</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <SelectContent>
        {lines.map((line) => (
          <SelectItem key={line.id} value={line.id}>
            {line.name}
            {line.isDefault ? ' (default)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
